import { fetchStudyDetailsDrawer, handleUpdateStudy } from '@/entities/study';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError } from '@/shared/lib/toastNotification';
import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import type React from 'react';
import { formatDataStudy } from './studyInfoDrawer.h';
import type { StudyInfoDrawerProps } from './studyInfoDrawer';

type StudyInfoDrawerState = {
  isScroll: boolean;
  isOpenEditModal: boolean;
  data: any[];
  studyNote: string;
  originalStudyNote: string;
  isSaving: boolean;
  isAbortedStudy: boolean;
};

const useStudyInfoDrawer = ({
  visible,
  fetchStudyId,
  handleClickCloseButton,
}: Pick<StudyInfoDrawerProps, 'visible' | 'fetchStudyId' | 'handleClickCloseButton'>) => {
  const [state, setState] = useMergeState<StudyInfoDrawerState>({
    isScroll: false,
    isOpenEditModal: false,
    data: [],
    studyNote: '',
    originalStudyNote: '',
    isSaving: false,
    isAbortedStudy: false,
  });

  /**
   * Opens or closes the study-note edit modal from the drawer's edit affordance. Inverts the isOpenEditModal flag in local state, memoized against its current value.
   *
   * @returns Nothing.
   */
  const toggleDisplayEditModal = useCallback(() => {
    setState({ isOpenEditModal: !state.isOpenEditModal });
  }, [state.isOpenEditModal]);

  /**
   * Builds the field list backing the study-notes edit modal. Returns a single descriptor named studyNotes carrying the current study-note draft from local state, memoized so the array is only rebuilt when that draft changes.
   *
   * @returns An array with one input descriptor for the study-notes field.
   */
  const generateInputList = useCallback(
    () => [
      {
        name: 'studyNotes',
        value: state.studyNote,
      },
    ],
    [state.studyNote],
  );

  /**
   * Records keystrokes in the study-notes editor as the user types. Writes the incoming value into the studyNote field of local state and ignores the field name, since the modal edits only that one field.
   *
   * @param name - Name of the edited field; unused because only the study note is editable.
   * @param value - Current text of the study-note editor.
   * @returns Nothing.
   */
  const onChangeStudyNote = (name: string, value: string) => {
    setState({ studyNote: value });
  };

  /**
   * Persists the edited study note for the study shown in the drawer. Sets a saving flag, sends the note through handleUpdateStudy, then deep-clones the drawer rows and writes the saved note onto the Study Notes row before clearing the modal and saving flags; on failure it logs, raises a Save Study Note toast and still closes the modal.
   *
   * @returns A promise resolving once the note has been saved and the drawer rows refreshed.
   */
  const onClickSaveStudyNote = async () => {
    try {
      setState({ isSaving: true });
      const study = await handleUpdateStudy(state.data[0].id, { studyNote: state.studyNote });
      const dataClone = _.cloneDeep(state.data);
      const ele = _.find(dataClone, (x) => x?.header === 'Study Notes');
      if (ele) {
        ele.content = study?.studyNote;
      }
      setState({
        data: dataClone,
        studyNote: study?.studyNote ?? '',
        originalStudyNote: study?.studyNote ?? '',
        isOpenEditModal: false,
        isSaving: false,
      });
    } catch (error: any) {
      console.error('Failed to save study note: ', error);
      toastrError(error.message, 'Save Study Note');
      setState({ isOpenEditModal: false, isSaving: false });
    }
  };

  /**
   * Abandons an in-progress study-note edit. Restores studyNote from the originalStudyNote captured when the study loaded and closes the edit modal, memoized against that original value.
   *
   * @returns Nothing.
   */
  const onClickCloseStudyNote = useCallback(() => {
    setState({
      studyNote: state.originalStudyNote,
      isOpenEditModal: false,
    });
  }, [state.originalStudyNote]);

  useUpdateEffect(() => {
    if (visible) {
      const myDiv = document.getElementById('drawer-body-container');
      if (myDiv) {
        myDiv.scrollTo({ top: 0 });
      }
    }
  }, [visible]);

  /**
   * Loads a study's detail rows into the info drawer. Fetches the study, formats it into drawer rows wired to the edit-modal toggle, and stores the rows along with the study note as both draft and original plus an aborted-status flag; a Study not found error toasts Study was deleted and closes the drawer, any other error just toasts its message.
   *
   * @param id - Identifier of the study whose details are loaded.
   * @returns A promise resolving once the drawer rows and study note are in state.
   */
  const handleFetchDataStudy = async (id: string) => {
    try {
      const result = await fetchStudyDetailsDrawer(id);
      const data = formatDataStudy(result, toggleDisplayEditModal);
      setState({
        data,
        originalStudyNote: (result as any)?.studyNote ?? '',
        studyNote: (result as any)?.studyNote ?? '',
        isAbortedStudy: (result as any)?.status === 'Aborted',
      });
    } catch (error: any) {
      console.error('Failed to fetch study: ', error);

      if (error.message === 'Error: Study not found') {
        toastrError('Study was deleted', 'Error');
        handleClickCloseButton();
        return;
      }

      toastrError(error.message, 'Error');
    }
  };

  useEffect(() => {
    if (visible) {
      handleFetchDataStudy(fetchStudyId);
    }
  }, [fetchStudyId, visible]);

  /**
   * Tracks whether the study info drawer body has been scrolled away from the top so the header can render its shadow. Reads scrollTop off the scrolled div and flips the isScroll flag in local state only when the top or not-top condition disagrees with the flag already stored.
   *
   * @param e - Scroll event from the drawer's scrollable container.
   * @returns Nothing.
   */
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e?.target) {
      const { scrollTop } = e.target as HTMLDivElement;
      if (scrollTop === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (scrollTop !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    }
  };

  /**
   * Applies a pushed study-updated notification to the study info drawer so an edit made elsewhere shows immediately. Ignores the message unless the loaded study id matches, then deep-clones the drawer rows, writes the incoming note onto the Study Notes row and stores it as both the displayed and original note.
   *
   * @param msg - Study-updated payload carrying the study id and its new studyNote.
   * @returns Nothing.
   */
  const handleStudyUpdated = (msg: any) => {
    if (state.data && state.data.length > 0) {
      const dataClone = _.cloneDeep(state.data);
      if (dataClone[0].id === msg.id) {
        const ele = _.find(dataClone, (x) => x?.header === 'Study Notes');
        if (ele) {
          ele.content = msg?.studyNote;
          setState({
            data: dataClone,
            studyNote: msg?.studyNote ?? '',
            originalStudyNote: msg?.studyNote ?? '',
          });
        }
      }
    }
  };

  useEffect(() => {
    const studyUpdatedListener = emitter.addListener(
      EMITTER_CONSTANTS.STUDY_UPDATED_EVENT,
      (msg: any) => {
        handleStudyUpdated(msg);
      },
    );
    return () => {
      studyUpdatedListener.remove();
    };
  }, [state.data]);

  return {
    state,
    toggleDisplayEditModal,
    generateInputList,
    onChangeStudyNote,
    onClickSaveStudyNote,
    onClickCloseStudyNote,
    handleScroll,
  };
};

export default useStudyInfoDrawer;
